import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sapService } from '../services/sapService';
import api from '../services/api';
import {
  RefreshCw,
  Database,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Package,
  Activity,
  Server,
  AlertCircle,
  Upload,
  Zap
} from 'lucide-react';

const SAPIntegration = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showGRNModal, setShowGRNModal] = useState(false);
  const [showMockGRNModal, setShowMockGRNModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [mockPostResult, setMockPostResult] = useState(null);

  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['sap-dashboard-stats'],
    queryFn: () => sapService.getDashboardStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch recent logs
  const { data: logsData } = useQuery({
    queryKey: ['sap-logs'],
    queryFn: () => sapService.getRequestLogs({ ordering: '-created_at', page_size: 10 }),
    refetchInterval: 30000,
  });

  // Fetch purchase orders
  const { data: purchaseOrders } = useQuery({
    queryKey: ['sap-purchase-orders'],
    queryFn: () => sapService.getPurchaseOrders({ ordering: '-po_date', page_size: 20 }),
  });

  // Fetch local purchase orders for sync stats
  const { data: localPOs } = useQuery({
    queryKey: ['local-purchase-orders'],
    queryFn: async () => {
      const response = await api.get('/procurement/purchase-orders/');
      return response.data;
    },
  });

  // Fetch pending GRNs (ready for SAP posting)
  const { data: pendingGRNs } = useQuery({
    queryKey: ['pending-grns'],
    queryFn: async () => {
      const response = await api.get('/qc/grns/', { params: { status: 'APPROVED' } });
      return response.data;
    },
  });

  // Calculate local PO sync stats
  const poSyncStats = {
    total: localPOs?.results?.length || localPOs?.length || 0,
    synced: (localPOs?.results || localPOs || []).filter(po => po.sap_sync_status === 'SYNCED').length,
    pending: (localPOs?.results || localPOs || []).filter(po => po.sap_sync_status === 'PENDING').length,
    failed: (localPOs?.results || localPOs || []).filter(po => po.sap_sync_status === 'FAILED').length,
    open: (localPOs?.results || localPOs || []).filter(po => po.status === 'OPEN').length,
    closed: (localPOs?.results || localPOs || []).filter(po => po.status === 'CLOSED').length,
  };

  // Sync Purchase Orders Mutation
  const syncPOMutation = useMutation({
    mutationFn: (data) => sapService.syncPurchaseOrders(data),
    onSuccess: (data) => {
      alert(`Purchase order sync queued! Task ID: ${data.task_id}`);
      queryClient.invalidateQueries(['sap-dashboard-stats']);
      setShowSyncModal(false);
    },
    onError: (error) => {
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Post GRN Mutation
  const postGRNMutation = useMutation({
    mutationFn: ({ grnId, configName }) => sapService.postGRN(grnId, configName),
    onSuccess: (data) => {
      alert(`GRN posting queued! Task ID: ${data.task_id}`);
      queryClient.invalidateQueries(['sap-dashboard-stats']);
      setShowGRNModal(false);
    },
    onError: (error) => {
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Mock Post GRN Mutation (Demo - synchronous posting)
  const mockPostGRNMutation = useMutation({
    mutationFn: (grnId) => sapService.mockPostGRN(grnId),
    onSuccess: (data) => {
      setMockPostResult(data);
      queryClient.invalidateQueries(['sap-dashboard-stats']);
      queryClient.invalidateQueries(['pending-grns']);
      queryClient.invalidateQueries(['sap-logs']);
    },
    onError: (error) => {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const StatCard = ({ title, value, icon: Icon, color, details }) => (
    <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <Icon className={`h-6 w-6 ${color}`} />
      </div>
      <div className="text-3xl font-bold text-gray-900 mb-3">{value || '--'}</div>
      {details && (
        <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100">
          {details.map((detail, idx) => (
            <div key={idx}>
              <div className="text-xs text-gray-500">{detail.label}</div>
              <div className="text-sm font-semibold text-gray-700">{detail.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const OperationCard = ({ title, description, icon: Icon, onClick, loading }) => (
    <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-indigo-500 transition-all">
      <div className="flex items-start space-x-4">
        <div className="p-3 bg-indigo-100 rounded-lg">
          <Icon className="h-6 w-6 text-indigo-600" />
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">{title}</h4>
          <p className="text-sm text-gray-600 mb-4">{description}</p>
          <button
            onClick={onClick}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                Processing...
              </>
            ) : (
              <>
                <Icon className="h-4 w-4 mr-2" />
                {title}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const StatusBadge = ({ status }) => {
    const colors = {
      SUCCESS: 'bg-green-100 text-green-800',
      FAILED: 'bg-red-100 text-red-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      OPEN: 'bg-blue-100 text-blue-800',
      POSTED: 'bg-green-100 text-green-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  // Sync PO Form Handler
  const handleSyncPO = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    syncPOMutation.mutate({
      vendor_code: formData.get('vendor_code') || null,
      days_back: parseInt(formData.get('days_back')),
      config_name: formData.get('config_name'),
    });
  };

  // Post GRN Form Handler
  const handlePostGRN = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    postGRNMutation.mutate({
      grnId: parseInt(formData.get('grn_id')),
      configName: formData.get('config_name'),
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">SAP Integration</h1>
          <p className="text-gray-600 mt-1">Monitor and manage SAP system integration</p>
        </div>
        <button
          onClick={() => queryClient.invalidateQueries()}
          className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['dashboard', 'operations', 'purchase-orders', 'logs', 'configuration'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.replace('-', ' ')}
            </button>
          ))}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Purchase Orders"
              value={stats?.purchase_orders?.total}
              icon={FileText}
              color="text-blue-500"
              details={[
                { label: 'Open', value: stats?.purchase_orders?.open || 0 },
                { label: 'Synced Today', value: stats?.purchase_orders?.synced_today || 0 },
              ]}
            />
            <StatCard
              title="Material Documents"
              value={stats?.material_documents?.total}
              icon={Package}
              color="text-green-500"
              details={[
                { label: 'Posted', value: stats?.material_documents?.posted || 0 },
                { label: 'Failed', value: stats?.material_documents?.failed || 0 },
              ]}
            />
            <StatCard
              title="API Requests (24h)"
              value={stats?.api_requests?.total_24h}
              icon={Activity}
              color="text-purple-500"
              details={[
                { label: 'Success', value: stats?.api_requests?.success || 0 },
                { label: 'Failed', value: stats?.api_requests?.failed || 0 },
              ]}
            />
            <StatCard
              title="Connections"
              value={stats?.connections?.total}
              icon={Server}
              color="text-orange-500"
              details={[
                { label: 'Active', value: stats?.connections?.active || 0 },
                { label: 'Mock', value: stats?.connections?.mock || 0 },
              ]}
            />
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent API Activity</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logsData?.results?.length > 0 ? (
                    logsData.results.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{log.endpoint}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.method}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={log.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.execution_time_ms}ms
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No recent activity
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Operations Tab */}
      {activeTab === 'operations' && (
        <div className="space-y-6">
          {/* Demo Operations - Highlighted */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200 p-6">
            <div className="flex items-center mb-4">
              <Zap className="h-6 w-6 text-green-600 mr-2" />
              <h3 className="text-lg font-semibold text-green-800">Demo Operations (Instant)</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <OperationCard
                title="Mock Post GRN to SAP"
                description="Demo: Instantly post GRN to SAP with simulated response. Completes workflow automatically."
                icon={Upload}
                onClick={() => setShowMockGRNModal(true)}
                loading={mockPostGRNMutation.isPending}
              />
            </div>
          </div>

          {/* Standard Operations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <OperationCard
              title="Sync Purchase Orders"
              description="Fetch open purchase orders from SAP system for processing"
              icon={FileText}
              onClick={() => setShowSyncModal(true)}
            />
            <OperationCard
              title="Post GRN (Async)"
              description="Queue goods receipt note for posting to SAP material documents"
              icon={Package}
              onClick={() => setShowGRNModal(true)}
            />
            <OperationCard
              title="Create Return"
              description="Create return delivery for failed quality inspection"
              icon={XCircle}
              onClick={() => setShowReturnModal(true)}
            />
            <OperationCard
              title="Sync Inventory"
              description="Synchronize inventory snapshot from SAP system"
              icon={Database}
              onClick={() => alert('Sync Inventory - Feature coming soon!')}
            />
          </div>
        </div>
      )}

      {/* Purchase Orders Tab */}
      {activeTab === 'purchase-orders' && (
        <div className="space-y-6">
          {/* PO Sync Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{poSyncStats.total}</div>
              <div className="text-sm text-gray-500">Total POs</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{poSyncStats.synced}</div>
              <div className="text-sm text-gray-500">SAP Synced</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{poSyncStats.pending}</div>
              <div className="text-sm text-gray-500">Pending Sync</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{poSyncStats.failed}</div>
              <div className="text-sm text-gray-500">Failed</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{poSyncStats.open}</div>
              <div className="text-sm text-gray-500">Open</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-gray-600">{poSyncStats.closed}</div>
              <div className="text-sm text-gray-500">Closed</div>
            </div>
          </div>

          {/* Local PO Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Purchase Orders (Local System)</h2>
              <p className="text-sm text-gray-500 mt-1">Purchase orders in Nexgensis SCM with SAP sync status</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SAP PO</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SAP Sync</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(localPOs?.results || localPOs || []).length > 0 ? (
                    (localPOs?.results || localPOs || []).map((po) => (
                      <tr key={po.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {po.po_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {po.sap_po_number || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {po.supplier_name || po.supplier?.name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{parseFloat(po.total_value || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={po.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            po.sap_sync_status === 'SYNCED' ? 'bg-green-100 text-green-800' :
                            po.sap_sync_status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                            po.sap_sync_status === 'FAILED' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {po.sap_sync_status || 'UNKNOWN'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        No purchase orders found in local system
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Request Logs</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retries</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logsData?.results?.length > 0 ? (
                  logsData.results.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{log.endpoint}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.method}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={log.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.response_status_code || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.execution_time_ms}ms
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.retry_count}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'configuration' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">SAP Connection Settings</h3>
              <p className="text-sm text-gray-600">
                Configure your SAP system connection parameters for RFC communication
              </p>
            </div>

            <form className="space-y-6">
              {/* Connection Details Section */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 text-sm uppercase tracking-wide border-b pb-2">
                  Connection Details
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SAP System ID (SID) *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., DEV, PRD"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Three-character SAP system identifier</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Number *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 800"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">SAP client number (typically 3 digits)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Application Server Host *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., sap.example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Hostname or IP address of SAP server</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      System Number *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Instance number (00-99)</p>
                  </div>
                </div>
              </div>

              {/* Authentication Section */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 text-sm uppercase tracking-wide border-b pb-2">
                  Authentication
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      placeholder="SAP username"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* RFC Configuration Section */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 text-sm uppercase tracking-wide border-b pb-2">
                  RFC Configuration
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Language
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                      <option value="EN">English (EN)</option>
                      <option value="DE">German (DE)</option>
                      <option value="FR">French (FR)</option>
                      <option value="ES">Spanish (ES)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pool Size
                    </label>
                    <input
                      type="number"
                      defaultValue="5"
                      min="1"
                      max="20"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Number of concurrent RFC connections</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timeout (seconds)
                    </label>
                    <input
                      type="number"
                      defaultValue="60"
                      min="10"
                      max="300"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Trace Level
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                      <option value="0">Off</option>
                      <option value="1">Errors Only</option>
                      <option value="2">Warnings</option>
                      <option value="3">Info</option>
                      <option value="4">Debug</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Business Object Configuration */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 text-sm uppercase tracking-wide border-b pb-2">
                  Business Object Configuration
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plant Code
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Default plant for material movements</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Storage Location
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 0001"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Movement Type (GRN)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 101"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Movement type for goods receipt</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Movement Type (Return)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 122"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Movement type for returns</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center space-x-2"
                >
                  <Server className="h-4 w-4" />
                  <span>Test Connection</span>
                </button>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span>Save Configuration</span>
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Connection Status Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Connection Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Last Successful Connection</span>
                <span className="text-sm font-medium text-gray-900">--</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Total Requests (24h)</span>
                <span className="text-sm font-medium text-gray-900">--</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Failed Requests (24h)</span>
                <span className="text-sm font-medium text-gray-900">--</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Average Response Time</span>
                <span className="text-sm font-medium text-gray-900">--</span>
              </div>
            </div>
          </div>

          {/* Help Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 mb-2">SAP Integration Requirements</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>SAP NetWeaver RFC SDK must be installed on the server</li>
                  <li>RFC user must have required authorizations (BAPI_GOODSMVT_CREATE)</li>
                  <li>Network connectivity to SAP application server required</li>
                  <li>Firewall rules must allow RFC communication (typically port 33XX)</li>
                </ul>
                <p className="text-sm text-blue-800 mt-3">
                  For detailed setup instructions, refer to the <a href="#" className="underline font-medium">SAP Integration Documentation</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync PO Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Sync Purchase Orders</h3>
            <form onSubmit={handleSyncPO} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor Code (Optional)
                </label>
                <input
                  type="text"
                  name="vendor_code"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Leave empty for all vendors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Days Back
                </label>
                <input
                  type="number"
                  name="days_back"
                  defaultValue="30"
                  min="1"
                  max="365"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SAP Configuration
                </label>
                <select
                  name="config_name"
                  defaultValue="default"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="default">Default</option>
                </select>
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowSyncModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={syncPOMutation.isPending}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {syncPOMutation.isPending ? 'Syncing...' : 'Start Sync'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Post GRN Modal */}
      {showGRNModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Post GRN to SAP</h3>
            <form onSubmit={handlePostGRN} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GRN ID
                </label>
                <input
                  type="number"
                  name="grn_id"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter GRN ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SAP Configuration
                </label>
                <select
                  name="config_name"
                  defaultValue="default"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="default">Default</option>
                </select>
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowGRNModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={postGRNMutation.isPending}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {postGRNMutation.isPending ? 'Posting...' : 'Post GRN'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mock Post GRN Modal (Demo) */}
      {showMockGRNModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Zap className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold">Mock Post GRN to SAP (Demo)</h3>
              </div>
              <button
                onClick={() => {
                  setShowMockGRNModal(false);
                  setMockPostResult(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            {!mockPostResult ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    <strong>Demo Mode:</strong> This will instantly simulate posting a GRN to SAP.
                    The workflow will automatically transition to COMPLETED and the dock will be released.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select GRN to Post
                  </label>
                  {pendingGRNs?.results?.length > 0 || pendingGRNs?.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {(pendingGRNs?.results || pendingGRNs || []).map((grn) => (
                        <div
                          key={grn.id}
                          className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                          onClick={() => mockPostGRNMutation.mutate(grn.id)}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-medium">{grn.grn_number}</div>
                              <div className="text-sm text-gray-500">
                                PO: {grn.purchase_order_number || grn.purchase_order?.po_number || 'N/A'} •
                                Value: ₹{parseFloat(grn.total_value || 0).toLocaleString()}
                              </div>
                            </div>
                            <button
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                              disabled={mockPostGRNMutation.isPending}
                            >
                              {mockPostGRNMutation.isPending ? 'Posting...' : 'Post'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No approved GRNs pending SAP posting</p>
                      <p className="text-sm">Complete QC inspection first to generate GRNs</p>
                    </div>
                  )}
                </div>

                <div className="text-sm text-gray-500">
                  <strong>Or enter GRN ID manually:</strong>
                </div>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    id="manual-grn-id"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Enter GRN ID"
                  />
                  <button
                    onClick={() => {
                      const grnId = document.getElementById('manual-grn-id').value;
                      if (grnId) mockPostGRNMutation.mutate(parseInt(grnId));
                    }}
                    disabled={mockPostGRNMutation.isPending}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {mockPostGRNMutation.isPending ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Success Message */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
                    <span className="font-semibold text-green-800">{mockPostResult.message}</span>
                  </div>
                </div>

                {/* SAP Response */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">SAP Response</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Material Document:</span>
                      <div className="font-mono font-semibold">{mockPostResult.sap_response?.MaterialDocument}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Document Year:</span>
                      <div className="font-mono font-semibold">{mockPostResult.sap_response?.MaterialDocumentYear}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Posting Date:</span>
                      <div className="font-semibold">{mockPostResult.sap_response?.PostingDate}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Movement Type:</span>
                      <div className="font-semibold">{mockPostResult.sap_response?.MovementType}</div>
                    </div>
                  </div>
                </div>

                {/* GRN Info */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-3">GRN Updated</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700">GRN Number:</span>
                      <div className="font-semibold">{mockPostResult.grn?.grn_number}</div>
                    </div>
                    <div>
                      <span className="text-blue-700">Status:</span>
                      <div className="font-semibold">{mockPostResult.grn?.status}</div>
                    </div>
                    <div>
                      <span className="text-blue-700">Total Value:</span>
                      <div className="font-semibold">₹{parseFloat(mockPostResult.grn?.total_value || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="text-blue-700">PO Status:</span>
                      <div className="font-semibold">{mockPostResult.po_status || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Workflow Status */}
                {mockPostResult.workflow_completed && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2" />
                      <span className="text-purple-800">
                        Workflow completed! Truck status updated and dock released.
                      </span>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    setMockPostResult(null);
                    setShowMockGRNModal(false);
                  }}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SAPIntegration;
